/**
 * What a record of this type would hold — derived from the TYPE, never fetched.
 *
 * Source: `type-composer.jsx:2010-2056` (`sampleRowsFor` / `sampleNameFor`) plus the
 * health strip at `:1829-1836`. The prototype reads values from a hard-coded `SAMPLES`
 * table and per-period evaluation targets, because nothing in it is connected. Neither
 * has a platform surrogate at TYPE level: targets are per-sphere data and there is no
 * sample-value registry. So each row states what the type can honestly say about it —
 * that a value is recorded here, that the attachment's definition is missing from this
 * workspace, or that a required field has nobody supplying it — which is exactly the
 * class of thing the preview exists to surface before publish.
 *
 * Health drivers come from `statusFactors` (types/spherePoints.d.ts), the platform's
 * own model for "what rolls up into this sphere's status", rather than from the Health
 * SECTION, whose information architecture this spec defers.
 */
import type { Schema, SchemaProperty } from 'types/schema';
import type { AspectRow } from 'types/aspect';

const KEY = 'studio.typeEditor.composer.sideRail';

export interface SampleRecordRow {
  /** Stable react key — the aspect's system name or the property's name. */
  key: string;
  label: string;
  /** `null` renders the contract's em-dash placeholder (`type-composer.jsx:1820`). */
  value: string | null;
  /** The row's qualifier, when the type has one to make. */
  note?: string;
  /** Nothing will render here until someone acts — the prototype's `data-vacant`. */
  vacant?: boolean;
}

export interface SampleHealthDriver {
  key: string;
  label: string;
  weight: number;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The synthetic value a type-level preview can state for a property with no default. */
function placeholderValue(property: SchemaProperty, t: Translate): string {
  switch (property.type) {
    case 'boolean':
      return t('studio.typeEditor.composer.sample.value.boolean');
    case 'number':
    case 'unit':
      return '0';
    case 'date':
    case 'datetime':
      return t('studio.typeEditor.composer.sample.value.date');
    case 'array':
      return t('studio.typeEditor.composer.sample.value.array');
    case 'reference':
      return t('studio.typeEditor.composer.sample.value.reference');
    default:
      return t('studio.typeEditor.composer.sample.value.text');
  }
}

/**
 * Composed aspects first, then the type's OWN properties — the contract's order
 * (`sampleRowsFor` returns `[...rows, ...ownRows]`). Inherited properties and those
 * contributed by an aspect are excluded: the aspect's own row already speaks for them,
 * and an inherited field is the ancestor's statement, not this type's.
 *
 * `catalog === null` means the workspace registry has not been read yet, which is
 * "unknown", never "missing" — an unresolved row is only claimed once a list has arrived.
 */
export function sampleRecordRows(
  definition: Schema | null,
  catalog: AspectRow[] | null,
  t: Translate
): SampleRecordRow[] {
  if (!definition) return [];
  const byName = catalog ? new Map(catalog.map((row) => [row.system_name, row])) : null;

  const aspectRows: SampleRecordRow[] = (definition.aspects ?? []).map((ref) => {
    const entry = byName?.get(ref.systemName);
    const label = ref.overrides?.label ?? entry?.name?.default ?? ref.systemName;
    if (byName && !entry) {
      return { key: ref.systemName, label, value: null, note: t(`${KEY}.sampleNotDefinedYet`), vacant: true };
    }
    const override = ref.overrides?.default_value;
    const value =
      override !== undefined && override !== null && override !== ''
        ? String(override)
        : t(`${KEY}.sampleRecorded`);
    // Bands are the type's judgement rule; the number they judge is per-sphere, so the
    // preview says where the value comes from rather than inventing one.
    const note = ref.evaluation?.bands ? t(`${KEY}.sampleJudgedAgainstTarget`) : undefined;
    return { key: ref.systemName, label, value, note };
  });

  const ownRows: SampleRecordRow[] = (definition.properties ?? [])
    .filter((property) => !property.hidden && !property.is_inherited && !property.aspect_system_name)
    .map((property) => {
      const label = property.label ?? property.name;
      const hasDefault =
        property.default_value !== undefined && property.default_value !== null && property.default_value !== '';
      if (hasDefault) return { key: property.name, label, value: String(property.default_value) };
      if (property.required) {
        return { key: property.name, label, value: null, note: t(`${KEY}.sampleRequiredNote`), vacant: true };
      }
      return { key: property.name, label, value: placeholderValue(property, t) };
    });

  return [...aspectRows, ...ownRows];
}

/** `type-composer.jsx:131-138` — the drivers that are actually counted, in declared order. */
export function sampleHealthDrivers(definition: Schema | null, t: Translate): SampleHealthDriver[] {
  const config = definition?.statusFactors;
  if (!config?.enabled) return [];
  return (config.factors ?? []).map((factor, index) => ({
    key: `${factor.factorType}-${index}`,
    label: t(`${KEY}.driver.${factor.factorType}`),
    weight: factor.weight,
  }));
}

/**
 * `sampleNameFor` (`:2052`) hard-codes a friendly name per prototype fixture and falls
 * back to `Sample <type>`; only the fallback generalises, so it is the whole rule here.
 */
export function sampleRecordName(definition: Schema | null, t: Translate): string {
  return t(`${KEY}.sampleName`, { type: definition?.name ?? '' });
}
